import { useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle } from "../../components/ui/dialog";
import { http, money } from "../../lib/api";
import { apiError } from "../../context/AuthContext";
import { OrderView, StatusBadge } from "../../components/shop/OrderView";
import { useAdminList, AdminHead } from "./adminUi";

const Shipment = ({ order, onDone }) => {
  const [t, setT] = useState({ carrier: "USPS", number: "", url: "" });
  const save = async (e) => {
    e.preventDefault();
    try {
      onDone((await http.post(`/admin/orders/${order.order_number}/shipment`, { ...t, url: t.url || null })).data);
      toast.success("Tracking saved — customer emailed");
      setT({ ...t, number: "", url: "" });
    } catch (err) {
      toast.error(apiError(err));
    }
  };
  return (
    <form onSubmit={save} className="rounded-2xl bg-sand p-4 space-y-2" data-testid="shipment-form">
      <p className="font-bold text-navy text-sm">Add tracking (emails the customer)</p>
      <div className="grid sm:grid-cols-3 gap-2">
        <input required value={t.carrier} onChange={(e) => setT({ ...t, carrier: e.target.value })} placeholder="Carrier" className="h-10 rounded-xl border px-3 text-sm" data-testid="shipment-carrier" />
        <input required minLength={4} value={t.number} onChange={(e) => setT({ ...t, number: e.target.value })} placeholder="Tracking #" className="h-10 rounded-xl border px-3 text-sm" data-testid="shipment-number" />
        <input type="url" value={t.url} onChange={(e) => setT({ ...t, url: e.target.value })} placeholder="Tracking URL (optional)" className="h-10 rounded-xl border px-3 text-sm" data-testid="shipment-url" />
      </div>
      <button className="h-10 px-4 rounded-full bg-navy text-white text-sm font-bold" data-testid="shipment-save">Save & notify</button>
    </form>
  );
};

const Fulfillment = ({ o, onDone }) => {
  const f = o.fulfillment || {};
  const call = async (kind) => {
    try {
      onDone((await http.post(`/admin/printify/orders/${o.order_number}/${kind}`)).data);
      toast.success(kind === "submit" ? "Submitted to Printify" : "Status refreshed");
    } catch (e) {
      toast.error(apiError(e));
    }
  };
  return (
    <div className="rounded-2xl border border-border p-4 text-sm text-navy" data-testid="fulfillment-panel">
      <p className="font-bold">Printify fulfillment: <span data-testid="fulfillment-status">{(f.status || "").replace("_", " ")}</span>{f.printify_status && <span className="text-navy/50"> · Printify: {f.printify_status}</span>}</p>
      {f.printify_order_id && <p className="mt-1">Printify order <a className="underline text-[#0078a0]" href="https://printify.com/app/orders" target="_blank" rel="noreferrer">{f.printify_order_id}</a>{f.status === "on_hold" && " — approve it in Printify to start production"}</p>}
      {f.costs && <p className="mt-1 text-navy/60">Printify cost: production ${f.costs.production.toFixed(2)} + shipping ${f.costs.shipping.toFixed(2)}{f.costs.tax ? ` + tax $${f.costs.tax.toFixed(2)}` : ""} · Margin ≈ ${(o.total - o.tax - f.costs.production - f.costs.shipping - f.costs.tax).toFixed(2)}</p>}
      <div className="mt-3 flex gap-2">
        {o.payment_status === "paid" && ["not_submitted", "failed", "manual_required"].includes(f.status) && <button onClick={() => call("submit")} className="h-9 px-4 rounded-full bg-navy text-white font-bold" data-testid="printify-submit-button">{f.status === "not_submitted" ? "Submit to Printify" : "Retry submission"}</button>}
        {f.printify_order_id && <button onClick={() => call("refresh")} className="h-9 px-4 rounded-full border-2 border-navy font-bold" data-testid="printify-refresh-button">Refresh status</button>}
      </div>
    </div>
  );
};

const FILTERS = ["", "paid", "pending_payment", "in_production", "shipped", "cancelled", "refunded"];

export default function AdminOrders() {
  const [status, setStatus] = useState("");
  const { data, reload } = useAdminList(`/admin/orders${status ? `?status=${status}` : ""}`);
  const [sel, setSel] = useState(null);
  const act = async (kind) => {
    if (!window.confirm(kind === "refund" ? "Issue a full Stripe refund for this order?" : "Cancel this order (and refund if paid)?")) return;
    try {
      setSel((await http.post(`/admin/orders/${sel.order_number}/${kind}`)).data);
      toast.success(kind === "refund" ? "Refund issued" : "Order cancelled");
      reload();
    } catch (e) {
      toast.error(apiError(e));
    }
  };
  return (
    <div data-testid="admin-orders">
      <AdminHead title="Orders" sub="Orders are only marked paid after Stripe confirms payment.">
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="h-11 rounded-full border-2 border-border px-4 bg-white" data-testid="orders-status-filter">
          {FILTERS.map((f) => <option key={f} value={f}>{f ? f.replace("_", " ") : "All statuses"}</option>)}
        </select>
      </AdminHead>
      <div className="rounded-3xl bg-white shadow-sm overflow-x-auto">
        <table className="w-full min-w-[720px] text-sm">
          <thead className="text-left text-navy/50 text-xs uppercase tracking-wider"><tr><th className="p-4">Order</th><th>Customer</th><th>Total</th><th>Payment</th><th>Fulfillment</th><th>Date</th></tr></thead>
          <tbody>
            {data?.map((o) => (
              <tr key={o.order_number} onClick={() => setSel(o)} className="border-t border-border cursor-pointer hover:bg-sand/50" data-testid={`admin-order-row-${o.order_number}`}>
                <td className="p-4 font-bold text-navy">{o.order_number}</td>
                <td>{o.email}</td>
                <td className="font-semibold">{money(o.total)}</td>
                <td><StatusBadge status={o.status} /></td>
                <td className="text-navy/60">{o.fulfillment?.status?.replace("_", " ")}{o.fulfillment?.errors?.length ? " ⚠" : ""}</td>
                <td className="text-navy/60">{new Date(o.created_at).toLocaleDateString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {data && !data.length && <p className="p-8 text-center text-navy/50" data-testid="admin-orders-empty">No orders yet.</p>}
      </div>
      <Dialog open={!!sel} onOpenChange={(o) => !o && setSel(null)}>
        <DialogContent className="max-w-2xl rounded-3xl max-h-[90vh] overflow-y-auto" data-testid="admin-order-dialog">
          <DialogTitle className="sr-only">Order detail</DialogTitle>
          {sel && (
            <>
              <p className="text-sm text-navy/60">Customer: <b>{sel.email}</b> · Stripe payment: {sel.payment_status}</p>
              <OrderView o={sel} />
              <div>
                <h4 className="font-bold text-navy text-sm">Timeline</h4>
                <ul className="mt-2 text-xs text-navy/70 space-y-1">{sel.timeline?.map((t, i) => <li key={i}>{new Date(t.at).toLocaleString()} — <b>{t.status}</b> · {t.note}</li>)}</ul>
              </div>
              {sel.fulfillment?.errors?.length > 0 && <div className="rounded-2xl bg-red-50 p-3 text-xs text-red-700">{sel.fulfillment.errors.map((e, i) => <p key={i}>{e}</p>)}</div>}
              <Fulfillment o={sel} onDone={(o) => { setSel(o); reload(); }} />
              {sel.payment_status === "paid" && <Shipment order={sel} onDone={(o) => { setSel(o); reload(); }} />}
              <div className="flex gap-2">
                <button disabled={sel.payment_status !== "paid"} onClick={() => act("refund")} className="h-11 px-5 rounded-full bg-tpink text-white font-bold disabled:opacity-40" data-testid="admin-refund-button">Refund</button>
                <button disabled={["cancelled", "refunded"].includes(sel.status)} onClick={() => act("cancel")} className="h-11 px-5 rounded-full border-2 border-navy font-bold text-navy disabled:opacity-40" data-testid="admin-cancel-button">Cancel order</button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
